import type { BudgetEngine } from '../core'
import type {
  BudgetCommand,
  BudgetCommandResult,
  BudgetDocument,
  BudgetEvent,
  BudgetEventType,
  BudgetListener,
} from '../types'

/**
 * Framework-agnostic bindings for UI layers (React later, Vue, mobile, etc.).
 * These are NOT React hooks — no JSX, no components.
 *
 * Future React adapter (module layer) can wrap:
 *   const api = createBudgetBindings(engine)
 *   useSyncExternalStore(api.subscribe, api.getSnapshot)
 */
export interface BudgetBindings {
  dispatch: (command: BudgetCommand) => BudgetCommandResult
  getSnapshot: () => BudgetDocument | null
  subscribe: (listener: () => void) => () => void
  on: (type: BudgetEventType | '*', listener: BudgetListener) => () => void
  validate: () => ReturnType<BudgetEngine['validate']>
  undo: () => BudgetCommandResult
  redo: () => BudgetCommandResult
  exportJSON: () => string
  importJSON: (raw: string) => BudgetCommandResult
}

export function createBudgetBindings(engine: BudgetEngine): BudgetBindings {
  return {
    dispatch: (command) => engine.dispatch(command),
    getSnapshot: () => engine.peek(),
    subscribe: (listener) =>
      engine.on('*', () => {
        listener()
      }),
    on: (type, listener) => engine.on(type, listener),
    validate: () => engine.validate(),
    undo: () => engine.undo(),
    redo: () => engine.redo(),
    exportJSON: () => engine.exportJSON(),
    importJSON: (raw) => engine.importJSON(raw),
  }
}

/** Convenience: listen only to document mutations. */
export function onBudgetDocumentChange(
  engine: BudgetEngine,
  listener: (document: BudgetDocument | null, event: BudgetEvent) => void,
): () => void {
  return engine.on('*', (event) => {
    listener(engine.peek(), event)
  })
}
